import { useEffect, useRef } from 'react';

/** 팝업 파일뷰어(ImagesView)에서 보내는 동기화 메시지 타입 */
export const CLAIM_VIEWER_SYNC_TYPE = 'CERP_CLAIM_VIEWER_SYNC';

/**
 * 팝업에서 연 문서 목록만 병합합니다. (한 종류만 열었을 때 나머지 서류가 사라지지 않도록)
 * @param {any[]} prev
 * @param {any[]} incoming
 * @param {any[]} removed
 * @param {{ imgIds?: number[], noIdKeys?: string[] }} [viewerKeys]
 */
export function mergeClaimDocuments(prev, incoming, removed, viewerKeys) {
  const prevList = Array.isArray(prev) ? prev : [];
  const incomingList = Array.isArray(incoming) ? incoming : [];
  const removedList = Array.isArray(removed) ? removed : [];

  const removedIds = new Set(
    removedList.map((r) => Number(r.imgId)).filter((id) => !Number.isNaN(id))
  );

  const vImg = viewerKeys?.imgIds;
  const vNo = viewerKeys?.noIdKeys;
  const hasViewerKeys =
    (Array.isArray(vImg) && vImg.length > 0) ||
    (Array.isArray(vNo) && vNo.length > 0);

  const incomingByImgId = new Map(
    incomingList
      .filter((i) => i.imgId != null)
      .map((i) => [Number(i.imgId), i])
  );
  const incomingByNoIdKey = new Map(
    incomingList
      .filter((i) => i.imgId == null)
      .map((i) => [`${i.documentCode}|${i.file?.name || ''}`, i])
  );

  if (!hasViewerKeys) {
    let next = prevList.filter(
      (p) => !p.imgId || !removedIds.has(Number(p.imgId))
    );
    next = next.map((item) => {
      if (item.imgId != null && incomingByImgId.has(Number(item.imgId))) {
        return { ...item, ...incomingByImgId.get(Number(item.imgId)) };
      }
      return item;
    });
    for (const inc of incomingList) {
      if (inc.imgId != null) {
        if (!next.some((n) => Number(n.imgId) === Number(inc.imgId)))
          next.push(inc);
      } else {
        const k = `${inc.documentCode}|${inc.file?.name || ''}`;
        if (
          !next.some(
            (n) =>
              n.imgId == null &&
              `${n.documentCode}|${n.file?.name || ''}` === k
          )
        )
          next.push(inc);
      }
    }
    return next;
  }

  const viewerImgSet = new Set((vImg || []).map(Number));
  const viewerNoSet = new Set(vNo || []);

  const out = [];
  for (const item of prevList) {
    if (item.imgId != null) {
      const id = Number(item.imgId);
      if (removedIds.has(id)) continue;
      if (viewerImgSet.has(id)) {
        const inc = incomingByImgId.get(id);
        if (inc) out.push({ ...item, ...inc });
        continue;
      }
      out.push(item);
      continue;
    }
    const nk = `${item.documentCode}|${item.file?.name || ''}`;
    if (viewerNoSet.has(nk)) {
      const inc = incomingByNoIdKey.get(nk);
      if (inc) out.push({ ...item, ...inc });
      continue;
    }
    out.push(item);
  }

  for (const inc of incomingList) {
    if (inc.imgId != null) {
      const id = Number(inc.imgId);
      if (viewerImgSet.has(id)) continue;
      if (
        !prevList.some((p) => Number(p.imgId) === id) &&
        !out.some((o) => Number(o.imgId) === id)
      )
        out.push(inc);
    } else {
      const nk = `${inc.documentCode}|${inc.file?.name || ''}`;
      if (viewerNoSet.has(nk)) continue;
      if (
        !out.some(
          (o) =>
            o.imgId == null &&
            `${o.documentCode}|${o.file?.name || ''}` === nk
        )
      )
        out.push(inc);
    }
  }

  return out;
}

function inferPartnerScope(images, removed) {
  const first = (images && images[0]) || (removed && removed[0]) || null;
  const code = first?.documentCode || '';
  return String(code).startsWith('P_') ? 'partner' : 'customer';
}

/**
 * 클레임 상세 등에서 `ImagesView` 팝업이 postMessage로 보낸 서류 목록을 반영합니다.
 * @param {{
 *   setDocs: Function,
 *   setFileItems: Function,
 *   setDeleteImgs?: Function,
 *   setPartnerFile?: Function,
 *   setPartnerDeleteImgs?: Function,
 *   setSelectedPartner?: Function,
 * }} p
 */
export function useClaimViewerSync({
  setDocs,
  setFileItems,
  setDeleteImgs,
  setPartnerFile,
  setPartnerDeleteImgs,
  setSelectedPartner,
}) {
  const setDeleteImgsRef = useRef(setDeleteImgs);
  setDeleteImgsRef.current = setDeleteImgs;
  const setPartnerDeleteImgsRef = useRef(setPartnerDeleteImgs);
  setPartnerDeleteImgsRef.current = setPartnerDeleteImgs;

  useEffect(() => {
    const onMsg = (e) => {
      if (!e.data || e.data.type !== CLAIM_VIEWER_SYNC_TYPE) return;
      if (e.origin !== window.location.origin) return;
      const { images, removed, viewerKeys } = e.data;
      if (!Array.isArray(images)) return;

      const scope = inferPartnerScope(images, removed || []);

      if (
        scope === 'partner' &&
        typeof setPartnerFile === 'function' &&
        typeof setSelectedPartner === 'function'
      ) {
        setPartnerFile((prev) =>
          mergeClaimDocuments(prev, images, removed || [], viewerKeys)
        );
        setSelectedPartner((prev) =>
          prev.map((p) => ({
            ...p,
            partnerDocuments: mergeClaimDocuments(
              p.partnerDocuments || [],
              images,
              removed || [],
              viewerKeys
            ),
          }))
        );
        if (
          Array.isArray(removed) &&
          removed.length &&
          typeof setPartnerDeleteImgsRef.current === 'function'
        ) {
          setPartnerDeleteImgsRef.current((prev) => [...prev, ...removed]);
        }
      } else {
        setFileItems((prev) =>
          mergeClaimDocuments(prev, images, removed || [], viewerKeys)
        );
        setDocs((prev) =>
          mergeClaimDocuments(prev, images, removed || [], viewerKeys)
        );
        if (
          Array.isArray(removed) &&
          removed.length &&
          typeof setDeleteImgsRef.current === 'function'
        ) {
          setDeleteImgsRef.current((prev) => [...prev, ...removed]);
        }
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [
    setDocs,
    setFileItems,
    setPartnerFile,
    setSelectedPartner,
  ]);
}
